import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { PortalDashboardObject } from "@ServerTypes/portal/portal.schema";

export interface HelpSectionState {
  display: boolean | null;
  module: string;
}

// Portal dashboard modal state — purely frontend UI state
export interface PortalDashboardDataState {
  isModalVisible: boolean;
  title: string;
  component: string | null;
}

/**
 * The patch payload `setModalVisiblity` accepts. NOT `Partial<PortalDashboardDataState>`: that makes
 * every key merely OPTIONAL, and under `exactOptionalPropertyTypes` an optional key may be ABSENT
 * but never present-holding-`undefined`. type-fest's `ExactObject` is a homomorphic mapping that
 * re-emits each optional member as `k?: V | undefined`, so a payload constrained by
 * `Exact<Partial<State>, T>` is not assignable back to `Partial<State>` — the wrapper below could not
 * forward its own argument. Naming the `| undefined` arm here fixes that. The `Exact<>` guard is
 * UNCHANGED: the key set is identical, so excess keys still map to `never` (the three
 * `@ts-expect-error` directives at dashboardWidgetCard.tsx:145-150 still fire).
 */
type PortalDashboardDataPatch = { [K in keyof PortalDashboardDataState]?: PortalDashboardDataState[K] | undefined };

const initialPortalDashboardDataState: PortalDashboardDataState = {
  isModalVisible: false,
  title: "",
  component: null,
};

const portalDashboardDataSlice = createSlice({
  name: "portalDashboardData",
  initialState: initialPortalDashboardDataState,
  reducers: {
    setPortalData: (_state: PortalDashboardDataState, action: PayloadAction<PortalDashboardDataState>) =>
      action.payload,
    // Draft-mutating form (identical to the sibling `projectDashboard` slice). A returned
    // `{ ...state, ...action.payload }` does not type-check under `exactOptionalPropertyTypes`:
    // the spread of a patch whose members are declared `V | undefined` yields `V | undefined` for
    // every member (TS keeps the `undefined` arm of an optional source property once eOPT is on),
    // which is not the slice's own state type. Mutating the Immer draft applies exactly the same
    // own enumerable keys, so the next state is byte-for-byte what the spread produced.
    //
    // BUG: a payload member that is present and holds `undefined` is copied through, so
    // `title`/`component` can end up `undefined` in a state that declares them non-nullable —
    // `dashboardWidgetCard.tsx:145` passes `widget.title || _.get(widget, "data.title")`, which is
    // `undefined` for a widget with neither. Behaviour left as-is (dropping the key instead would
    // silently keep the PREVIOUS widget's title in the maximise modal); the real fix is for that
    // call site to supply a fallback title.
    setModalVisiblity: (state: PortalDashboardDataState, action: PayloadAction<PortalDashboardDataPatch>) => {
      Object.assign(state, action.payload);
    },
  },
});

const helpSectionStateSlice = createSlice({
  name: "helpSectionState",
  initialState: { display: null, module: "general" } as HelpSectionState,
  reducers: {
    setHelpSectionState: (_state: HelpSectionState, action: PayloadAction<HelpSectionState>) => action.payload,
  },
});

const reducerHeaderSlice = createSlice({
  name: "reducerHeader",
  initialState: true,
  reducers: {
    setReduceHeader: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

const portalDashboardObjectSlice = createSlice({
  name: "portalDashboardObject",
  initialState: {} as PortalDashboardObject,
  reducers: {
    setPortalDashboardObject: (_state: PortalDashboardObject, action: PayloadAction<PortalDashboardObject>) =>
      action.payload,
  },
});

const { setPortalData: _setPortalDataRaw, setModalVisiblity: _setModalVisiblityRaw } = portalDashboardDataSlice.actions;
const { setPortalDashboardObject: _setPortalDashboardObjectRaw } = portalDashboardObjectSlice.actions;
const { setHelpSectionState: _setHelpSectionStateRaw } = helpSectionStateSlice.actions;
export const { setReduceHeader } = reducerHeaderSlice.actions;

/**
 * Object-payload action creators are wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 *
 * KEEP THE CONSTRAINT FORM. A pass on 2026-09-03 moved this to the parameter form
 * (`<T extends P>(payload: T & Exact<P, T>)`) to get past `exactOptionalPropertyTypes`, reasoning
 * that excess keys would still map to `never`. They do not: with `T` inferred from the argument, the
 * intersection is checked against the already-inferred `T` and excess keys pass. It was caught
 * because two `@ts-expect-error` directives at dashboardWidgetCard.tsx went UNUSED — the compiler
 * proving the guard had stopped firing. Reverted. If eOPT reports this creator again, fix the CALL
 * SITE (spread the possibly-undefined member conditionally); do not loosen the guard.
 *
 * 2026-09-03, second eOPT pass: the reported error was on `payload` INSIDE this wrapper, so no
 * call-site change could clear it — `Exact<>`'s own output was not assignable to the raw creator's
 * `Partial<State>` parameter. Fixed by naming the payload type (see the patch type above) rather
 * than by touching the `Exact<>` form, which is still the constraint form and still rejects excess
 * keys.
 */
export const setPortalData = <T extends Exact<PortalDashboardDataState, T>>(payload: T) => _setPortalDataRaw(payload);
export const setModalVisiblity = <T extends Exact<PortalDashboardDataPatch, T>>(payload: T) =>
  _setModalVisiblityRaw(payload);
export const setPortalDashboardObject = <T extends Exact<PortalDashboardObject, T>>(payload: T) =>
  _setPortalDashboardObjectRaw(payload);
export const setHelpSectionState = <T extends Exact<HelpSectionState, T>>(payload: T) =>
  _setHelpSectionStateRaw(payload);

export const portalDashboardData = portalDashboardDataSlice.reducer;
export const helpSectionState = helpSectionStateSlice.reducer;
export const onReduceHeader = reducerHeaderSlice.reducer;
export const portalDashboardObject = portalDashboardObjectSlice.reducer;

export default portalDashboardDataSlice.reducer;
